import { createSlice } from '@reduxjs/toolkit';
import { cellKey, blendedKey, resolveStsFromProfile } from '../pages/merchants/tat-wizard/constants';

// Reverse-derives freight directions and mode × size checkboxes from a list of
// active service type codes (from API 2 active_service_types). Mirrors the
// inverse of resolveStsFromProfile() so the Profile stage is pre-populated
// with the merchant's existing onboarding selections.
function deriveProfileFromSTs(activeSts) {
  const s = new Set(activeSts || []);
  return {
    directions: {
      forward:      s.has('FWD_SURFACE_REG') || s.has('FWD_SURFACE_LARGE') || s.has('FWD_AIR_REG') || s.has('FWD_AIR_LARGE') || s.has('SDD_FWD_REG') || s.has('SDD_FWD_LARGE') || s.has('SC_NDD_FWD_REG') || s.has('SC_NDD_FWD_LARGE') || s.has('INTERCITY_NDD_REG') || s.has('INTERCITY_NDD_LARGE') || s.has('H2TO4') || s.has('SUB2H') || s.has('HV_FWD_REG') || s.has('HV_FWD_LARGE'),
      reverse_noqc: s.has('REV_REG_NOQC') || s.has('REV_LARGE_NOQC') || s.has('HV_REVERSE_REG') || s.has('HV_REVERSE_LARGE'),
      reverse_qc:   s.has('REV_REG_QC')   || s.has('REV_LARGE_QC'),
    },
    modes: {
      surface:      { reg: s.has('FWD_SURFACE_REG'),    large: s.has('FWD_SURFACE_LARGE') },
      air:          { reg: s.has('FWD_AIR_REG'),         large: s.has('FWD_AIR_LARGE') },
      sdd:          { reg: s.has('SDD_FWD_REG'),         large: s.has('SDD_FWD_LARGE') },
      sc_ndd:       { reg: s.has('SC_NDD_FWD_REG'),      large: s.has('SC_NDD_FWD_LARGE') },
      intercity_ndd:{ reg: s.has('INTERCITY_NDD_REG'),   large: s.has('INTERCITY_NDD_LARGE') },
      h2to4:        { reg: s.has('H2TO4'),               large: false },
      sub2h:        { reg: s.has('SUB2H'),               large: false },
      hv:           { reg: s.has('HV_FWD_REG') || s.has('HV_REVERSE_REG'), large: s.has('HV_FWD_LARGE') || s.has('HV_REVERSE_LARGE') },
    },
  };
}

function freshAgreement() {
  return {
    pdfDataUrl: null,
    filename: '',
    // 'idle' (no extraction run) → 'extracting' (AI working) → 'done' (editor unlocked)
    extractionState: 'idle',
    hasTatTable: false,
    confidence: 0,
    // analyst must Preview & Confirm the extracted TATs before leaving the editor step
    confirmed: false,
  };
}

function freshState() {
  return {
    flowMode: null,
    selectedMerchant: null,
    merchantOid: '',
    merchantName: '',
    // Lowercased identifiers (account code OR parent) the merchant has pricing
    // configured for — derived from merchant.sla. Empty = no pricing loaded, so
    // consumers should fall back to showing everything.
    pricedKeys: [],
    agreement: freshAgreement(),
    profile: {
      directions: { forward: false, reverse_noqc: false, reverse_qc: false },
      modes: {
        surface: { reg: false, large: false },
        air: { reg: false, large: false },
        sdd: { reg: false, large: false },
        sc_ndd: { reg: false, large: false },
        intercity_ndd: { reg: false, large: false },
        h2to4: { reg: false, large: false },
        sub2h: { reg: false, large: false },
        hv: { reg: false, large: false },
      },
    },
    selectedSTs: [],
    stage: 0,
    showAll: false,
    modeByST: {},
    tat: {},
    tatBlended: {},
    // Snapshot of the TAT values as loaded from an existing config (update flow).
    // null = no existing config (fresh setup). Used to detect "TATs are being
    // changed" so we can warn before overwriting live TAT on submit.
    tatOriginal: null,
    edits: {},
    source: {},
    confidence: {},
    paneCollapse: { agreement: false, editor: false },
    expandedSTs: {},
    uploadResults: [],
    fixes: {},
    zipReady: false,
    standardTat: {},
    extractionFillMode: null,
    viewer: {
      open: false,
      group: 'service_type',
      sortKey: 'tat_max',
      sortDir: 'asc',
      filters: {},
    },
  };
}

const initialState = freshState();

const tatWizardSlice = createSlice({
  name: 'tatWizard',
  initialState,
  reducers: {
    // --- Entry / flow selection ---------------------------------------------
    chooseFlow(state, { payload }) {
      state.flowMode = payload; // 'new' | 'existing'
      state.stage = 0;
      state.selectedMerchant = null;
      state.merchantOid = '';
      state.merchantName = '';
    },
    // Dashboard → "Set up TAT": begin the new-merchant TAT flow for a specific merchant.
    // Resets ALL merchant-specific TAT data so no stale values from a previous merchant
    // or previous extraction bleed through when the AI extraction returns nothing.
    startSetup(state, { payload }) {
      state.flowMode = 'new';
      state.selectedMerchant = null;
      state.merchantOid = payload.oid;
      state.merchantName = payload.name;
      state.stage = 0;
      state.agreement = freshAgreement();
      state.selectedSTs = [];
      state.modeByST = {};
      state.tat = {};
      state.tatBlended = {};
      state.edits = {};
      state.source = {};
      state.confidence = {};
      state.uploadResults = [];
      state.fixes = {};
      state.zipReady = false;
      state.extractionFillMode = null;
      state.expandedSTs = {};
    },
    // Dashboard → "Update TAT": begin the existing-merchant update flow.
    startUpdate(state, { payload }) {
      state.flowMode = 'existing';
      state.selectedMerchant = payload.oid;
      state.merchantOid = payload.oid;
      state.merchantName = payload.name;
      state.stage = 0;
      state.agreement = freshAgreement();
      state.selectedSTs = [];
      state.modeByST = {};
      state.tat = {};
      state.tatBlended = {};
      state.edits = {};
      state.source = {};
      state.confidence = {};
      state.uploadResults = [];
      state.fixes = {};
      state.zipReady = false;
      state.extractionFillMode = null;
      state.expandedSTs = {};
    },
    switchFlow(state) {
      state.flowMode = null;
      state.selectedMerchant = null;
      state.merchantOid = '';
      state.merchantName = '';
      state.stage = 0;
      state.agreement = freshAgreement();
    },
    selectExistingMerchant(state, { payload }) {
      state.selectedMerchant = payload.oid;
      state.merchantOid = payload.oid;
      state.merchantName = payload.name;
    },
    clearSelectedMerchant(state) {
      state.selectedMerchant = null;
      state.merchantOid = '';
      state.merchantName = '';
    },
    // --- Agreement upload + AI extraction -----------------------------------
    setAgreementPdf(state, { payload }) {
      state.agreement.pdfDataUrl = payload.pdfDataUrl;
      state.agreement.filename = payload.filename;
      state.agreement.extractionState = 'idle';
      state.agreement.hasTatTable = false;
      state.agreement.confidence = 0;
      state.agreement.confirmed = false;
    },
    clearAgreementPdf(state) {
      state.agreement = freshAgreement();
    },
    startExtraction(state) {
      state.agreement.extractionState = 'extracting';
      state.agreement.confirmed = false;
    },
    finishExtraction(state, { payload }) {
      state.agreement.extractionState = 'done';
      state.agreement.hasTatTable = !!(payload && payload.hasTatTable);
      state.agreement.confidence = (payload && payload.confidence) || 0;
      state.agreement.confirmed = false;
    },
    confirmExtraction(state) {
      state.agreement.confirmed = true;
    },
    // --- Operating profile (freight direction + mode × size) ----------------
    toggleDirection(state, { payload }) {
      state.profile.directions[payload] = !state.profile.directions[payload];
      state.selectedSTs = resolveStsFromProfile(state.profile).map((s) => s.code);
    },
    toggleMode(state, { payload }) {
      const { mode, size } = payload;
      state.profile.modes[mode][size] = !state.profile.modes[mode][size];
      state.selectedSTs = resolveStsFromProfile(state.profile).map((s) => s.code);
    },
    setStage(state, { payload }) {
      if (payload <= state.stage + 1) state.stage = payload;
    },
    nextStage(state) {
      // 6 stages: Continuation · Profile · Extract · Review · Submit · Done
      if (state.stage < 5) state.stage += 1;
    },
    prevStage(state) {
      if (state.stage > 0) state.stage -= 1;
    },
    setShowAll(state, { payload }) {
      state.showAll = payload;
    },
    togglePane(state, { payload }) {
      state.paneCollapse[payload] = !state.paneCollapse[payload];
    },
    toggleExpandST(state, { payload }) {
      state.expandedSTs[payload] = !state.expandedSTs[payload];
    },
    expandAllSTs(state, { payload }) {
      (payload || state.selectedSTs || []).forEach((st) => { state.expandedSTs[st] = true; });
    },
    collapseAllSTs(state) {
      state.expandedSTs = {};
    },
    switchMode(state, { payload }) {
      const cur = state.modeByST[payload];
      state.modeByST[payload] = cur === 'blended' ? 'courier_wise' : 'blended';
    },
    setCell(state, { payload }) {
      const { st, parent, z, bound, value } = payload;
      if (!state.tat[st]) state.tat[st] = {};
      if (!state.tat[st][parent]) state.tat[st][parent] = {};
      state.tat[st][parent][`z${z}_${bound}`] = value;
      const key = cellKey(st, parent, z, bound);
      state.edits[key] = true;
      state.source[key] = 'manual';
      // Persist courier-wise mode so Review/Submit read from the correct map
      if (!state.modeByST[st]) state.modeByST[st] = 'courier_wise';
    },
    setBlendCell(state, { payload }) {
      const { st, z, bound, value } = payload;
      if (!state.tatBlended[st]) state.tatBlended[st] = {};
      state.tatBlended[st][`z${z}_${bound}`] = value;
      const key = blendedKey(st, z, bound);
      state.edits[key] = true;
      state.source[key] = 'manual';
      // Persist blended mode so Review/Submit read from the correct map
      if (!state.modeByST[st]) state.modeByST[st] = 'blended';
    },
    markZipReady(state, { payload }) {
      state.zipReady = payload !== false;
    },
    setUploadResults(state, { payload }) {
      state.uploadResults = payload;
    },
    replaceUploadResult(state, { payload }) {
      // Match on both accountCode AND service_type so blended entries
      // (accountCode: '') for different STs are not mixed up.
      const idx = state.uploadResults.findIndex(
        (r) => r.accountCode === payload.accountCode && r.service_type === payload.service_type,
      );
      if (idx >= 0) state.uploadResults[idx] = payload;
    },
    markFix(state, { payload }) {
      state.fixes[payload] = true;
    },
    setViewerOpen(state, { payload }) {
      state.viewer.open = payload;
    },
    setViewerGroup(state, { payload }) {
      state.viewer.group = payload;
    },
    setViewerSort(state, { payload }) {
      const { key, dir } = payload;
      if (key) state.viewer.sortKey = key;
      if (dir) state.viewer.sortDir = dir;
    },
    setViewerFilter(state, { payload }) {
      const { col, value } = payload;
      if (value === '' || value === undefined) {
        delete state.viewer.filters[col];
      } else {
        state.viewer.filters[col] = value;
      }
    },
    clearViewerFilters(state) {
      state.viewer.filters = {};
    },
    // --- API 2: merchant context loaded on wizard entry ----------------------
    initFromContext(state, { payload }) {
      // payload = API 2 response data: { oid, name, active_service_types, tat_status }
      state.merchantOid = payload.oid;
      state.merchantName = payload.name;
      if (Array.isArray(payload.active_service_types) && payload.active_service_types.length) {
        state.selectedSTs = payload.active_service_types;
        // Auto-populate Profile stage checkboxes from the merchant's existing
        // onboarding data — the analyst should not have to re-select directions
        // and modes that were already configured in the pricing wizard.
        state.profile = deriveProfileFromSTs(payload.active_service_types);
      }
    },
    // --- Fallback: enrich context with data from GET /api/merchant/{id} -------
    // Called after initFromContext. Fills in any fields that the TAT-specific API
    // didn't return, using the full merchant record as a fallback.
    updateContextFromMerchant(state, { payload }) {
      const m = payload?.data ?? payload;
      if (!m) return;

      // Name fallback
      if (!state.merchantName && m.name) state.merchantName = m.name;

      // Couriers/account codes the merchant has pricing for — from the committed
      // rate cards (merchant.sla). cplist[0] is the account code; we keep every
      // cplist value (lowercased) so consumers can match by AC code or parent.
      if (Array.isArray(m.sla)) {
        const keys = new Set();
        m.sla
          .filter((e) => e && e.active !== false)
          .forEach((e) => (e.cplist || []).forEach((c) => c && keys.add(String(c).toLowerCase())));
        if (keys.size) state.pricedKeys = [...keys];
      }

      // Service types fallback — derive from resolvedServiceTypes in pricing wizard data
      if (!state.selectedSTs?.length && Array.isArray(m.resolvedServiceTypes) && m.resolvedServiceTypes.length) {
        state.selectedSTs = m.resolvedServiceTypes;
        state.profile = deriveProfileFromSTs(m.resolvedServiceTypes);
      }

      // If selectedSTs came from TAT API but profile directions are still all-false
      // (e.g. TAT API returned active_service_types but deriveProfileFromSTs wasn't called yet),
      // re-derive profile from freightDirections + resolvedServiceTypes on the merchant record.
      if (!state.profile.directions.forward && !state.profile.directions.reverse_noqc && !state.profile.directions.reverse_qc) {
        const dirs = m.freightDirections || [];
        if (dirs.includes('FORWARD'))  state.profile.directions.forward      = true;
        if (dirs.includes('REVERSE'))  state.profile.directions.reverse_noqc = true;
        if (dirs.includes('RVP'))      state.profile.directions.reverse_qc   = true;
      }
    },
    // --- API 3: hydrate TAT values from existing config (update flow) ---------
    hydrateTatConfig(state, { payload }) {
      // payload = API 3 response data: { tat, tatBlended, modeByST, profile }
      if (payload.tat) state.tat = payload.tat;
      if (payload.tatBlended) state.tatBlended = payload.tatBlended;
      if (payload.modeByST) state.modeByST = payload.modeByST;
      // Snapshot what we loaded so submit can detect changes against live TAT.
      // Only set when the merchant actually has an existing config.
      if (payload.tat || payload.tatBlended) {
        state.tatOriginal = {
          tat: payload.tat ? JSON.parse(JSON.stringify(payload.tat)) : {},
          tatBlended: payload.tatBlended ? JSON.parse(JSON.stringify(payload.tatBlended)) : {},
        };
      }
    },
    // --- API 4: apply AI extraction result ------------------------------------
    applyExtractionResult(state, { payload }) {
      // payload = { fill_mode, extraction: { [ST]: { defaultZones, overrides } } }
      state.extractionFillMode = payload.fill_mode || 'manual';
      state.agreement.extractionState = 'done';
      state.agreement.hasTatTable = payload.has_tat_table || false;
      state.agreement.confidence = payload.confidence || 0;
      state.agreement.confirmed = false;

      // When AI finds no TAT table, clear any stale values so the editor starts blank.
      // Without this, data from a previous extraction or a previous merchant bleeds through.
      if (!payload.has_tat_table || payload.fill_mode === 'manual') {
        state.tat = {};
        state.tatBlended = {};
        state.source = {};
        state.confidence = {};
        state.edits = {};
      }

      const extraction = payload.extraction || {};
      Object.entries(extraction).forEach(([st, stData]) => {
        // Blended: defaultZones keyed by string zone "1"–"5"
        if (stData.defaultZones) {
          if (!state.tatBlended[st]) state.tatBlended[st] = {};
          Object.entries(stData.defaultZones).forEach(([zone, vals]) => {
            const z = Number(zone);
            state.tatBlended[st][`z${z}_min`] = vals.min;
            state.tatBlended[st][`z${z}_max`] = vals.max;
            state.source[`__BLEND__${st}__z${z}_min`] = vals.source || 'agreement';
            state.source[`__BLEND__${st}__z${z}_max`] = vals.source || 'agreement';
            state.confidence[`__BLEND__${st}__z${z}_min`] = vals.conf ?? 0;
            state.confidence[`__BLEND__${st}__z${z}_max`] = vals.conf ?? 0;
          });
        }
        // Courier-wise: overrides keyed by parent courier name
        if (stData.overrides) {
          if (!state.tat[st]) state.tat[st] = {};
          Object.entries(stData.overrides).forEach(([parent, zones]) => {
            if (!state.tat[st][parent]) state.tat[st][parent] = {};
            Object.entries(zones).forEach(([zone, vals]) => {
              const z = Number(zone);
              state.tat[st][parent][`z${z}_min`] = vals.min;
              state.tat[st][parent][`z${z}_max`] = vals.max;
              state.source[`${st}__${parent}__z${z}_min`] = vals.source || 'agreement';
              state.source[`${st}__${parent}__z${z}_max`] = vals.source || 'agreement';
              state.confidence[`${st}__${parent}__z${z}_min`] = vals.conf ?? 0;
              state.confidence[`${st}__${parent}__z${z}_max`] = vals.conf ?? 0;
            });
          });
        }
      });
    },
    // --- API 5: store industry-standard TAT reference -------------------------
    setStandardTat(state, { payload }) {
      state.standardTat = payload;
    },
    resetTatWizard() {
      return freshState();
    },
  },
});

export const {
  chooseFlow, startSetup, startUpdate, switchFlow, selectExistingMerchant, clearSelectedMerchant,
  setAgreementPdf, clearAgreementPdf, startExtraction, finishExtraction, confirmExtraction,
  toggleDirection, toggleMode,
  setStage, nextStage, prevStage,
  setShowAll, togglePane,
  toggleExpandST, expandAllSTs, collapseAllSTs,
  switchMode, setCell, setBlendCell,
  markZipReady, setUploadResults, replaceUploadResult, markFix,
  setViewerOpen, setViewerGroup, setViewerSort, setViewerFilter, clearViewerFilters,
  initFromContext, updateContextFromMerchant, hydrateTatConfig, applyExtractionResult, setStandardTat,
  resetTatWizard,
} = tatWizardSlice.actions;

export default tatWizardSlice.reducer;
